/**
 * Sparkline: a tiny trend line for KPI tiles and table cells, with a hover readout of the nearest point.
 *
 * Port of LAE `features/productivity/charts/Sparkline.jsx` (spec §3.2, §10.1 C2). Same geometry (3 px pad,
 * gaps break the line, a lone point between gaps gets a small dot, the last known point gets the end dot,
 * a constant series sits on the baseline) and the same portalled, viewport-clamped readout.
 *
 * Deliberate differences from LAE:
 * - Colours are EMERGE tokens: the line is the first chart token; a flat series uses the subtle foreground
 *   token; the hover rule uses the hairline token; the readout uses surface / hairline / foreground tokens.
 * - The readout is a data readout (label + value), never helper text. It is portalled to the body at the
 *   z-[60] popover rung, and it is dropped on scroll or resize because a fixed box would drift from its point.
 * - `className` is not a prop (spec §10.1 signature); callers position the wrapper instead.
 *
 * A null, undefined or non-finite value is a gap (e.g. a rate on a day with no calls): the line breaks there
 * and the readout shows "—" rather than plotting it as 0. Fewer than two points, or no known point, renders
 * nothing.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';

const PAD = 3; // room for the end dot, hover dot and stroke
const GAP = 8; // readout distance from the point
const EDGE = 8; // minimum distance from the viewport edge

export type SparklineProps = {
  values: ReadonlyArray<number | null | undefined>;
  labels?: ReadonlyArray<string | null | undefined>;
  formatValue?: (value: number) => string;
  width?: number;
  height?: number;
  ariaLabel: string;
};

type Hover = { i: number; ax: number; ay: number };

function defaultFormat(v: number): string {
  return Number.isFinite(v) ? v.toLocaleString('en-IN') : '—';
}

export function Sparkline({ values, labels, formatValue, width = 96, height = 28, ariaLabel }: SparklineProps) {
  const [hover, setHover] = useState<Hover | null>(null);

  useEffect(() => {
    if (!hover) return undefined;
    const clear = () => setHover(null);
    window.addEventListener('scroll', clear, true);
    window.addEventListener('resize', clear);
    return () => {
      window.removeEventListener('scroll', clear, true);
      window.removeEventListener('resize', clear);
    };
  }, [hover]);

  if (!Array.isArray(values) || values.length < 2) return null;

  const vals: Array<number | null> = values.map((v) => (typeof v === 'number' && Number.isFinite(v) ? v : null));
  const known = vals.filter((v): v is number => v !== null);
  if (known.length === 0) return null;
  const min = Math.min(...known);
  const max = Math.max(...known);
  const flat = max === min;

  const innerW = width - PAD * 2;
  const innerH = height - PAD * 2;
  const step = innerW / (vals.length - 1);
  const x = (i: number) => PAD + i * step;
  // A constant (or all-zero) series sits on the baseline rather than floating mid-cell.
  const y = (v: number) => (flat ? height - PAD : PAD + innerH - ((v - min) / (max - min)) * innerH);

  // One run of indices per stretch without gaps.
  const runs: number[][] = [];
  let run: number[] = [];
  vals.forEach((v, i) => {
    if (v === null) {
      if (run.length) runs.push(run);
      run = [];
    } else run.push(i);
  });
  if (run.length) runs.push(run);

  const last = vals.length - 1;
  let lastKnown = last;
  while (lastKnown > 0 && vals[lastKnown] === null) lastKnown -= 1;
  const lastKnownValue = vals[lastKnown] ?? null;

  const stroke = flat ? 'var(--fg-subtle)' : 'var(--chart-1)';
  const fmt = formatValue ?? defaultFormat;

  function onPointerMove(e: ReactPointerEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const sx = rect.width / width;
    const sy = rect.height / height;
    const px = (e.clientX - rect.left) / sx;
    const i = Math.max(0, Math.min(last, Math.round((px - PAD) / step)));
    const v = vals[i] ?? null;
    const ax = rect.left + x(i) * sx;
    const ay = rect.top + (v === null ? height / 2 : y(v)) * sy;
    setHover((h) => (h && h.i === i && h.ax === ax && h.ay === ay ? h : { i, ax, ay }));
  }

  const hi = hover?.i;
  const hiValue = hi === undefined ? null : (vals[hi] ?? null);
  const hoverLabel = hover && labels ? labels[hover.i] : undefined;

  return (
    <>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel}
        className="block shrink-0 overflow-visible"
        onPointerMove={onPointerMove}
        onPointerDown={onPointerMove}
        onPointerLeave={() => setHover(null)}
      >
        {/* Transparent hit area so hovering between points still tracks. */}
        <rect x="0" y="0" width={width} height={height} fill="transparent" />
        {hi !== undefined && (
          <line
            x1={x(hi).toFixed(1)}
            x2={x(hi).toFixed(1)}
            y1="0"
            y2={height}
            stroke="var(--hairline)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {runs.map((idx) => {
          const first = idx[0];
          if (first === undefined) return null;
          if (idx.length > 1) {
            return (
              <polyline
                key={first}
                points={idx.map((i) => `${x(i).toFixed(1)},${y(vals[i] ?? 0).toFixed(1)}`).join(' ')}
                fill="none"
                stroke={stroke}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          }
          if (first === lastKnown) return null;
          return (
            <circle key={first} cx={x(first).toFixed(1)} cy={y(vals[first] ?? 0).toFixed(1)} r="1.25" fill={stroke} />
          );
        })}
        {lastKnownValue !== null && (
          <circle cx={x(lastKnown).toFixed(1)} cy={y(lastKnownValue).toFixed(1)} r="2.5" fill={stroke} />
        )}
        {hi !== undefined && hiValue !== null && (
          <circle
            cx={x(hi).toFixed(1)}
            cy={y(hiValue).toFixed(1)}
            r="2.75"
            fill={stroke}
            stroke="var(--surface)"
            strokeWidth="1.5"
          />
        )}
      </svg>
      {hover &&
        createPortal(
          <SparkReadout
            ax={hover.ax}
            ay={hover.ay}
            label={hoverLabel ?? undefined}
            value={hiValue === null ? '—' : fmt(hiValue)}
          />,
          document.body,
        )}
    </>
  );
}

/**
 * Fixed-position readout: measured after mount, then placed above the point (below when there is no room)
 * and clamped to the viewport. Positioned through the DOM node so no extra render is needed.
 */
function SparkReadout({ ax, ay, label, value }: { ax: number; ay: number; label: string | undefined; value: string }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width: w, height: h } = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = ax - w / 2;
    left = Math.max(EDGE, Math.min(left, vw - w - EDGE));
    let top = ay - h - GAP;
    if (top < EDGE) top = Math.max(EDGE, Math.min(ay + GAP, vh - h - EDGE));
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
    el.style.visibility = 'visible';
  }, [ax, ay, label, value]);

  return (
    <div
      ref={ref}
      role="tooltip"
      style={{ position: 'fixed', left: 0, top: 0, visibility: 'hidden' }}
      className="pointer-events-none z-[60] flex w-max max-w-[calc(100vw-16px)] flex-wrap items-baseline gap-x-1.5 break-words rounded-md border border-border bg-surface px-2 py-1 text-xs leading-tight text-fg shadow-sm"
    >
      {label != null && label !== '' && <span className="tabular-nums text-fg-muted">{label}</span>}
      <span className="font-semibold tabular-nums text-fg">{value}</span>
    </div>
  );
}
